import { CombinedGraphQLErrors } from '@apollo/client';
import { getClient } from '@api/client';
import { fragment } from '@api/fragment';
import { ServiceUserUtils } from '../../lib';
import ServiceUser from '../../types/ServiceUser';
import { getUsersAvailabilityActivityQuery } from './getServiceUsersQuery';
import {
  serviceUserFieldsFragment,
  serviceUserStatsFragment,
} from './serviceUserFragment';

async function getUsers(
  currentUserId: string,
  fetchStats = false,
): Promise<ServiceUser[]> {
  const { data, error } = await getClient().query({
    query: getUsersAvailabilityActivityQuery,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    variables: { skipStats: !fetchStats },
  });

  // temporary error handling to minimize retries CCP-390
  // we don't want to throw an error if there are a small amount of
  // status errors and the current user status is successful
  if (CombinedGraphQLErrors.is(error)) {
    const { errors } = error;

    const currentUserIndex: number | undefined = data?.users?.items?.findIndex(
      (user) => {
        const base = fragment(serviceUserFieldsFragment, user);
        return base.id === currentUserId;
      },
    );

    const statusErrors = errors.filter(
      (error) =>
        error.path && error.path.length === 4 && error.path[3] === 'stats',
    );

    // unique status errors by user index
    const uniqueStatusErrors = statusErrors.filter(
      (error1, index, array) =>
        array.findIndex((error2) => error1.path![2] === error2.path![2]) ===
        index,
    );

    // see if our current user has a status error
    const currentUserHasStatusError = uniqueStatusErrors.some((error) => {
      return error.path![2] === currentUserIndex;
    });

    // calc percentage of users with status errors
    const statusErrorPercentage =
      uniqueStatusErrors.length /
      Math.max(data?.users?.items?.length ?? 0, uniqueStatusErrors.length);

    // error out if we have non-status errors
    if (errors.length > statusErrors.length) {
      throw new Error(errors[0].message);
    }

    // error out if more than 30% of our users report status errors
    if (uniqueStatusErrors.length > 0 && statusErrorPercentage > 0.3) {
      throw new Error('Failed to get user statuses');
    }

    // error out if our current user has a status error
    if (currentUserHasStatusError) {
      throw new Error('Failed to get user status for current user');
    }
  }

  const users =
    data?.users?.items?.map((item) => {
      const base = fragment(serviceUserFieldsFragment, item);
      const statsFragment = fragment(serviceUserStatsFragment, item);

      const statusData =
        fetchStats && statsFragment
          ? ServiceUserUtils.parseAvailabilityActivityStatus(statsFragment)
          : undefined;

      return {
        ...base,
        stats: statsFragment?.stats ?? null,
        ...statusData,
      };
    }) ?? [];

  return users as unknown as ServiceUser[];
}

export default getUsers;
